/**
 * Security Service
 *
 * Provides security-related functionality including token validation,
 * session management, and activity tracking.
 *
 * Features:
 * - JWT token expiration checking
 * - Session timeout management with automatic logout
 * - User activity tracking
 * - Session warning notifications
 *
 * @example
 * ```typescript
 * constructor(private securityService: SecurityService) {}
 *
 * ngOnInit() {
 *   // Start session timeout tracking
 *   this.securityService.startSessionTimer();
 *
 *   // Listen for timeout events
 *   this.securityService.onSessionTimeout.subscribe(() => {
 *     this.handleSessionTimeout();
 *   });
 * }
 * ```
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Subject, interval, Subscription } from 'rxjs';
import { SECURITY_CONFIG } from '../constants/security.constants';

/**
 * JWT token payload interface
 */
interface JwtPayload {
  /**
   * Token expiration timestamp (seconds since epoch)
   */
  exp?: number;

  /**
   * Token issued at timestamp (seconds since epoch)
   */
  iat?: number;

  /**
   * Token issuer
   */
  iss?: string;

  /**
   * Token subject (usually user ID)
   */
  sub?: string;

  /**
   * Additional custom claims
   */
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class SecurityService implements OnDestroy {
  /**
   * Subject that emits when session timeout occurs
   * Components can subscribe to this to handle automatic logout
   */
  onSessionTimeout: Subject<void> = new Subject<void>();

  /**
   * Subject that emits when session warning should be shown
   * Emits the remaining time in seconds
   */
  onSessionWarning: Subject<number> = new Subject<number>();

  /**
   * Timestamp of last user activity
   */
  private lastActivityTime: number = Date.now();

  /**
   * Session timer subscription
   */
  private sessionTimerSubscription?: Subscription;

  /**
   * Session timeout in milliseconds
   */
  private readonly sessionTimeout: number = SECURITY_CONFIG.SESSION_TIMEOUT_MINUTES * 60 * 1000;

  /**
   * Session warning time in milliseconds before timeout
   */
  private readonly warningTime: number = SECURITY_CONFIG.SESSION_WARNING_MINUTES * 60 * 1000;

  /**
   * Flag to track if warning has been shown
   */
  private warningShown: boolean = false;

  ngOnDestroy(): void {
    this.stopSessionTimer();
  }

  /**
   * Checks if a JWT token has expired
   *
   * Parses the token and compares the expiration timestamp with current time.
   *
   * @param token - JWT token to check
   * @returns True if token is expired, false otherwise
   *
   * @example
   * ```typescript
   * const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...';
   * if (this.securityService.isTokenExpired(token)) {
   *   console.log('Token has expired');
   *   this.authService.logout();
   * }
   * ```
   */
  isTokenExpired(token: string): boolean {
    const expiration = this.getTokenExpiration(token);
    if (!expiration) {
      return true;
    }

    return expiration.getTime() < Date.now();
  }

  /**
   * Gets the expiration date from a JWT token
   *
   * @param token - JWT token to parse
   * @returns Expiration date or null if token is invalid
   *
   * @example
   * ```typescript
   * const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...';
   * const expiry = this.securityService.getTokenExpiration(token);
   * if (expiry) {
   *   console.log('Token expires at:', expiry);
   * }
   * ```
   */
  getTokenExpiration(token: string): Date | null {
    const payload = this.parseToken(token);
    if (!payload || !payload.exp) {
      return null;
    }

    // JWT exp is in seconds, JavaScript Date uses milliseconds
    return new Date(payload.exp * 1000);
  }

  /**
   * Safely parses a JWT token payload
   *
   * Extracts and decodes the payload section of the JWT token.
   * Returns null if the token is invalid or cannot be parsed.
   *
   * @param token - JWT token to parse
   * @returns Decoded token payload or null if invalid
   *
   * @example
   * ```typescript
   * const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...';
   * const payload = this.securityService.parseToken(token);
   * if (payload) {
   *   console.log('User ID:', payload.sub);
   *   console.log('Expires at:', new Date(payload.exp * 1000));
   * }
   * ```
   */
  parseToken(token: string): JwtPayload | null {
    if (!token || typeof token !== 'string') {
      return null;
    }

    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded) as JwtPayload;
    } catch (error) {
      console.error('Failed to parse JWT token:', error);
      return null;
    }
  }

  /**
   * Starts the session timeout timer
   *
   * Monitors user activity and emits timeout/warning events.
   * Should be called when user logs in or on app initialization.
   *
   * @example
   * ```typescript
   * // Start session tracking on login
   * this.authService.login(credentials).subscribe(() => {
   *   this.securityService.startSessionTimer();
   * });
   * ```
   */
  startSessionTimer(): void {
    // Stop existing timer if any
    this.stopSessionTimer();

    // Reset activity time
    this.resetSessionTimer();
    this.warningShown = false;

    // Check every 10 seconds
    this.sessionTimerSubscription = interval(10000).subscribe(() => {
      this.checkSessionTimeout();
    });
  }

  /**
   * Stops the session timeout timer
   *
   * Should be called on logout or component destruction.
   *
   * @example
   * ```typescript
   * this.authService.logout().subscribe(() => {
   *   this.securityService.stopSessionTimer();
   * });
   * ```
   */
  stopSessionTimer(): void {
    if (this.sessionTimerSubscription) {
      this.sessionTimerSubscription.unsubscribe();
      this.sessionTimerSubscription = undefined;
    }
    this.warningShown = false;
  }

  /**
   * Resets the session timer by updating the last activity time
   *
   * Should be called on user interactions (clicks, keyboard events, etc.)
   *
   * @example
   * ```typescript
   * @HostListener('document:click')
   * @HostListener('document:keypress')
   * onUserActivity() {
   *   this.securityService.resetSessionTimer();
   * }
   * ```
   */
  resetSessionTimer(): void {
    this.lastActivityTime = Date.now();
    this.warningShown = false;
  }

  /**
   * Gets the time remaining until session timeout
   *
   * @returns Remaining time in milliseconds
   *
   * @example
   * ```typescript
   * const remaining = this.securityService.getTimeUntilTimeout();
   * console.log(`Session expires in ${Math.floor(remaining / 1000)} seconds`);
   * ```
   */
  getTimeUntilTimeout(): number {
    const elapsed = Date.now() - this.lastActivityTime;
    const remaining = this.sessionTimeout - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Checks if session has timed out and emits appropriate events
   */
  private checkSessionTimeout(): void {
    const remaining = this.getTimeUntilTimeout();

    // Session has timed out
    if (remaining === 0) {
      this.onSessionTimeout.next();
      this.stopSessionTimer();
      return;
    }

    // Show warning if approaching timeout and not already shown
    if (remaining <= this.warningTime && !this.warningShown) {
      this.warningShown = true;
      this.onSessionWarning.next(Math.floor(remaining / 1000));
    }
  }
}
